import React from 'react';
import { SuperEmojiEffect } from '../../utils/types';
import './SuperEmojiEffects.css';

interface SuperEmojiEffectsProps {
  effects: SuperEmojiEffect[];
  windowSize: { width: number; height: number };
}

const SuperEmojiEffects: React.FC<SuperEmojiEffectsProps> = ({ effects, windowSize }) => {
  return (
    <div className="super-emoji-effects-container">
      {effects.map(effect => {
        if (!effect.isActive) return null;

        const progress = Math.min((performance.now() - effect.startTime) / effect.duration, 1);

        switch (effect.type) {
          case 'dance':
            return (
              <DanceEffect 
                key={effect.id} 
                effect={effect} 
                progress={progress}
              />
            );
          case 'sword':
            return (
              <SwordEffect 
                key={effect.id} 
                effect={effect} 
                progress={progress}
                windowSize={windowSize}
              />
            );
          case 'parachute':
            return (
              <ParachuteEffect 
                key={effect.id} 
                effect={effect} 
                progress={progress}
                windowSize={windowSize}
              />
            );
          case 'bomb':
            return (
              <BombEffect 
                key={effect.id} 
                effect={effect} 
                progress={progress}
                windowSize={windowSize}
              />
            );
          case 'fire':
            return (
              <FireEffect 
                key={effect.id} 
                effect={effect} 
                progress={progress}
                windowSize={windowSize}
              />
            );
          default:
            return null;
        }
      })}
    </div>
  );
};

// Dance Effect Component
const DanceEffect: React.FC<{ effect: SuperEmojiEffect; progress: number }> = ({ effect, progress }) => {
  if (!effect.positions || effect.positions.length === 0) return null;
  
  // Create a path that goes through all bounce points
  const bouncePoints = effect.positions;
  
  return (
    <>
      {/* Main dancing emoji */}
      <div
        className="dance-bounce-point smooth-dance-animation"
        style={{
          left: bouncePoints[0].x - 25, // Start at first bounce point
          top: bouncePoints[0].y - 25,
          '--dance-duration': `${effect.duration}ms`,
          '--bounce-point-0-x': `${bouncePoints[0]?.x || 0}px`,
          '--bounce-point-0-y': `${bouncePoints[0]?.y || 0}px`,
          '--bounce-point-1-x': `${bouncePoints[1]?.x || 0}px`,
          '--bounce-point-1-y': `${bouncePoints[1]?.y || 0}px`,
          '--bounce-point-2-x': `${bouncePoints[2]?.x || 0}px`,
          '--bounce-point-2-y': `${bouncePoints[2]?.y || 0}px`,
          '--bounce-point-3-x': `${bouncePoints[3]?.x || 0}px`,
          '--bounce-point-3-y': `${bouncePoints[3]?.y || 0}px`,
          '--bounce-point-4-x': `${bouncePoints[4]?.x || 0}px`,
          '--bounce-point-4-y': `${bouncePoints[4]?.y || 0}px`,
          zIndex: 1003
        } as React.CSSProperties}
      >
        💃
      </div>
      
      {/* Pulse effects at each landing point */}
      {bouncePoints.map((point, index) => (
        <div
          key={`pulse-${index}`}
          className="dance-pulse-effect"
          style={{
            left: point.x - 50, // Center the 100px pulse effect
            top: point.y - 50,   // Center the 100px pulse effect
            '--pulse-delay': `${(index * 0.25) * effect.duration}ms`,
            '--pulse-duration': `${effect.duration}ms`
          } as React.CSSProperties}
        />
      ))}
    </>
  );
};

// Sword Effect Component
const SwordEffect: React.FC<{ 
  effect: SuperEmojiEffect; 
  progress: number; 
  windowSize: { width: number; height: number };
}> = ({ effect, progress, windowSize }) => {
  // Generate sword trajectories
  const trajectories = [
    // Sword 1: Bottom-left to top-right
    [
      { x: 0, y: windowSize.height, rotation: 45 },
      { x: windowSize.width, y: 0, rotation: 45 }
    ],
    // Sword 2: Bottom-right to top-left
    [
      { x: windowSize.width, y: windowSize.height, rotation: -45 },
      { x: 0, y: 0, rotation: -45 }
    ]
  ];

  return (
    <>
      {trajectories.map((trajectory, index) => {
        const start = trajectory[0];
        const end = trajectory[1];
        
        // Calculate total distance for CSS animation
        const deltaX = end.x - start.x;
        const deltaY = end.y - start.y;
        
        return (
          <div
            key={`sword-${index}`}
            className="sword-effect smooth-sword-animation"
            style={{
              left: start.x - 25,
              top: start.y - 25,
              '--sword-delta-x': `${deltaX}px`,
              '--sword-delta-y': `${deltaY}px`,
              '--sword-duration': `${effect.duration}ms`,
              '--sword-rotation': `${start.rotation}deg`
            } as React.CSSProperties}
          >
            ⚔️
          </div>
        );
      })}
    </>
  );
};

// Parachute Effect Component
const ParachuteEffect: React.FC<{ 
  effect: SuperEmojiEffect; 
  progress: number; 
  windowSize: { width: number; height: number };
}> = ({ effect, progress, windowSize }) => {
  return (
    <>
      {effect.positions!.map((startPos, index) => {
        const fallDistance = windowSize.height + 100;
        
        return (
          <div
            key={`parachute-${index}`}
            className="parachute-effect smooth-parachute-animation"
            style={{
              left: startPos.x - 25,
              top: startPos.y - 25,
              '--parachute-fall-distance': `${fallDistance}px`,
              '--parachute-duration': `${effect.duration}ms`,
              zIndex: 1002 // Ensure parachutes are visible above other elements
            } as React.CSSProperties}
          >
            🪂
          </div>
        );
      })}
    </>
  );
};

// Bomb Effect Component
const BombEffect: React.FC<{ 
  effect: SuperEmojiEffect; 
  progress: number; 
  windowSize: { width: number; height: number };
}> = ({ effect, progress, windowSize }) => {
  const scale = 1 + progress * 10;
  const opacity = Math.max(0, 1 - progress);
  
  return (
    <div
      className="bomb-effect"
      style={{
        left: windowSize.width / 2 - 25,
        top: windowSize.height / 2 - 25,
        transform: `scale(${scale})`,
        opacity: opacity
      }}
    >
      💣
    </div>
  );
};

// Fire Effect Component
const FireEffect: React.FC<{ 
  effect: SuperEmojiEffect; 
  progress: number; 
  windowSize: { width: number; height: number };
}> = ({ effect, progress, windowSize }) => {
  const scale = 1 + progress * 20;
  const opacity = Math.max(0, 1 - progress);
  
  return (
    <div
      className="fire-effect"
      style={{
        left: windowSize.width / 2 - 25,
        top: windowSize.height / 2 - 25,
        transform: `scale(${scale})`,
        opacity: opacity
      }}
    >
      ❤️‍🔥
    </div>
  );
};

export default SuperEmojiEffects;